import React from 'react';
import '../../TestView/styles.scss';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import strings from 'utils/strings';
import {Link} from 'react-router-dom';
import handBookPdf from 'assets/DysmateHåndbok_v1.1_compressed.pdf';
import {
	CHILD_SCRNG_DLD_TEST_STEP_IDS,
	YOUTH_TEST_NORMING_STEP_IDS,
} from '../../../constants';
import {getPronoun} from 'utils/getPronouns';
import {formateTestName} from 'utils/formatTestName';

const TESTS_REQUIRED_TO_PASS = 3;

class Assessment extends React.Component {
	constructor(props) {
		super(props);
	}
	formatDldYouthScreeningSubtestNames(ids, subtestNamesById) {
		const names = ids
			.map((id) => subtestNamesById[id])
			.filter((name) => !!name);

		if (names.length === 0) {
			return '';
		}

		if (names.length === 1) {
			return names[0];
		}

		if (names.length === 2) {
			return `${names[0]} og ${names[1]}`;
		}

		return `${names.slice(0, -1).join(', ')} og ${names[names.length - 1]}`;
	}
	formatDldAdultScreeningSubtestNames(ids, subtestNamesById) {
		const names = ids
			.map((id) => subtestNamesById[id])
			.filter((name) => !!name);

		if (names.length === 0) return '';

		if (names.length === 1) return names[0];

		if (names.length === 2) {
			return `${names[0]} og ${names[1]}`;
		}

		return `${names.slice(0, -1).join(', ')} og ${names[names.length - 1]}`;
	}

	getNorwegianPossessiveName(name) {
		if (!name) return '';

		const trimmedName = name.trim();

		if (/[sxz]$/i.test(trimmedName)) {
			return `${trimmedName}'`;
		}

		return `${trimmedName}s`;
	}

	formatDldScreeningText(text, lowSubtestNames, firstName, isTestOwner) {
		const formattedText = strings.formatString(
			text,
			lowSubtestNames,
			firstName
		);

		const possessiveName = isTestOwner
			? this.getNorwegianPossessiveName(firstName)
			: 'kandidatens';

		return formattedText.split(`${firstName}s`).join(possessiveName);
	}

	formatDldChildScreeningText(
		text,
		lowSubtestNames,
		firstName,
		isTestOwner,
		testReportLang
	) {
		const formattedText = strings.formatString(
			text,
			lowSubtestNames,
			firstName
		);

		const possessiveName = isTestOwner
			? this.getNorwegianPossessiveName(firstName)
			: testReportLang === 'nn'
				? 'eleven sine'
				: 'elevens';

		return formattedText.split(`${firstName}s`).join(possessiveName);
	}

	getDldAdultScreeningWriteUpText(
		testResult,
		firstName,
		completeName,
		testReportLang
	) {
		const ids = {
			orddiktat: YOUTH_TEST_NORMING_STEP_IDS.ORDDIKTAT,
			ordkjedetesten: YOUTH_TEST_NORMING_STEP_IDS.ORDKJEDETESTEN,
			tulleordtesten: YOUTH_TEST_NORMING_STEP_IDS.TULLEORDTESTEN,
			skrivehastighet: YOUTH_TEST_NORMING_STEP_IDS.SKRIVEHASTIGHET,
			leseproven: YOUTH_TEST_NORMING_STEP_IDS['LESEPRØVEN'],
			ordforstaelse: YOUTH_TEST_NORMING_STEP_IDS['ORDFORSTÅELSE'],
			sprakforstaelse: YOUTH_TEST_NORMING_STEP_IDS['SPRAKFORSTÅELSE'],
		};

		const orderedIds = [
			ids.orddiktat,
			ids.ordkjedetesten,
			ids.skrivehastighet,
			ids.leseproven,
			ids.tulleordtesten,
			ids.sprakforstaelse,
			ids.ordforstaelse,
		];

		const subtestNamesById = {
			[ids.orddiktat]: 'Orddiktat',
			[ids.ordkjedetesten]: 'Ordkjedetest',
			[ids.tulleordtesten]: 'Tulleord',
			[ids.skrivehastighet]: 'Skrivehastighet',
			[ids.leseproven]: 'Leseprøven',
			[ids.ordforstaelse]: 'Ordforståelse',
			[ids.sprakforstaelse]: 'Setningsforståelse',
		};

		const scoresById = {};

		(testResult.subtests || []).forEach((subtest) => {
			if (!subtest) return;

			const rawScore = subtest.zScore;

			if (
				rawScore === null ||
				typeof rawScore === 'undefined' ||
				rawScore === ''
			) {
				return;
			}

			const score = Number(rawScore);

			if (!isNaN(score)) {
				scoresById[subtest.id] = score;
			}
		});

		const hasAllRequiredScores = orderedIds.every((id) =>
			Object.prototype.hasOwnProperty.call(scoresById, id)
		);

		if (!testResult.completedAt || !hasAllRequiredScores) {
			const incompleteText =
				testReportLang === 'nn'
					? strings._props.nn.localizedwriteuptextforIncompletedTest
					: strings.localizedwriteuptextforIncompletedTest;

			return strings.formatString(incompleteText, `${completeName}`);
		}

		const atOrBelow = (id, threshold) => scoresById[id] <= threshold;

		const isLow = (id) => atOrBelow(id, -1.0);
		const isSevere = (id) => atOrBelow(id, -1.5);

		const dyslexiaIds = [
			ids.orddiktat,
			ids.ordkjedetesten,
			ids.skrivehastighet,
			ids.leseproven,
			ids.tulleordtesten,
		];

		const dyslexiaOtherIds = [
			ids.ordkjedetesten,
			ids.skrivehastighet,
			ids.leseproven,
			ids.tulleordtesten,
		];

		const dyslexiaSpecificIds = [
			ids.orddiktat,
			ids.skrivehastighet,
			ids.tulleordtesten,
		];

		const languageSpecificIds = [ids.sprakforstaelse, ids.ordforstaelse];

		const sharedIds = [ids.ordkjedetesten, ids.leseproven];

		const languageIds = [
			ids.ordkjedetesten,
			ids.leseproven,
			ids.sprakforstaelse,
			ids.ordforstaelse,
		];

		const lowIds = orderedIds.filter((id) => isLow(id));

		const dyslexiaLowCount = dyslexiaIds.filter((id) => isLow(id)).length;

		const dyslexiaSevereOtherCount = dyslexiaOtherIds.filter((id) =>
			isSevere(id)
		).length;

		const meetsDyslexiaProfile =
			dyslexiaLowCount >= 3 ||
			dyslexiaSevereOtherCount >= 2 ||
			(isSevere(ids.orddiktat) &&
				dyslexiaOtherIds.some((id) => isLow(id)));

		const languageLowCount = languageIds.filter((id) => isLow(id)).length;

		const languageSevereCount = languageIds.filter((id) =>
			isSevere(id)
		).length;

		const meetsLanguageProfile =
			languageLowCount >= 3 || languageSevereCount >= 2;

		const hasDyslexiaSpecificLowScore = dyslexiaSpecificIds.some((id) =>
			isLow(id)
		);

		const hasLanguageSpecificLowScore = languageSpecificIds.some((id) =>
			isLow(id)
		);

		const isMixedProfile =
			lowIds.length >= 3 &&
			hasDyslexiaSpecificLowScore &&
			hasLanguageSpecificLowScore;

		const lowSubtestNames = this.formatDldAdultScreeningSubtestNames(
			lowIds,
			subtestNamesById
		);
		const adultScreeningStrings =
			testReportLang === 'nn' ? strings._props.nn : strings;

		const isSharedSevereDyslexiaPair =
			isSevere(ids.ordkjedetesten) &&
			isSevere(ids.leseproven) &&
			orderedIds
				.filter((id) => !sharedIds.includes(id))
				.every((id) => !isLow(id));

		if (isSharedSevereDyslexiaPair) {
			return this.formatDldScreeningText(
				adultScreeningStrings.dldAdultScreeningText1,
				lowSubtestNames,
				firstName,
				testResult.isTestOwner
			);
		}

		if (isMixedProfile) {
			return this.formatDldScreeningText(
				adultScreeningStrings.dldAdultScreeningText5,
				lowSubtestNames,
				firstName,
				testResult.isTestOwner
			);
		}

		if (meetsDyslexiaProfile && meetsLanguageProfile) {
			if (hasDyslexiaSpecificLowScore && !hasLanguageSpecificLowScore) {
				const text = isLow(ids.leseproven)
					? adultScreeningStrings.dldAdultScreeningText1
					: adultScreeningStrings.dldAdultScreeningText2;

				return this.formatDldScreeningText(
					text,
					lowSubtestNames,
					firstName,
					testResult.isTestOwner
				);
			}

			if (hasLanguageSpecificLowScore && !hasDyslexiaSpecificLowScore) {
				const text = isLow(ids.leseproven)
					? adultScreeningStrings.dldAdultScreeningText3
					: adultScreeningStrings.dldAdultScreeningText4;

				return this.formatDldScreeningText(
					text,
					lowSubtestNames,
					firstName,
					testResult.isTestOwner
				);
			}
		}

		if (meetsDyslexiaProfile) {
			const text = isLow(ids.leseproven)
				? adultScreeningStrings.dldAdultScreeningText1
				: adultScreeningStrings.dldAdultScreeningText2;

			return this.formatDldScreeningText(
				text,
				lowSubtestNames,
				firstName,
				testResult.isTestOwner
			);
		}

		if (meetsLanguageProfile) {
			const text = isLow(ids.leseproven)
				? adultScreeningStrings.dldAdultScreeningText3
				: adultScreeningStrings.dldAdultScreeningText4;

			return this.formatDldScreeningText(
				text,
				lowSubtestNames,
				firstName,
				testResult.isTestOwner
			);
		}

		if (lowIds.length >= 3) {
			return this.formatDldScreeningText(
				adultScreeningStrings.dldAdultScreeningText5,
				lowSubtestNames,
				firstName,
				testResult.isTestOwner
			);
		}

		return adultScreeningStrings.dldAdultScreeningText6;
	}

	getDldYouthScreeningWriteUpText(
		testResult,
		firstName,
		completeName,
		testReportLang
	) {
		const ids = {
			orddiktat: YOUTH_TEST_NORMING_STEP_IDS.ORDDIKTAT,
			ordkjedetesten: YOUTH_TEST_NORMING_STEP_IDS.ORDKJEDETESTEN,
			tulleordtesten: YOUTH_TEST_NORMING_STEP_IDS.TULLEORDTESTEN,
			skrivehastighet: YOUTH_TEST_NORMING_STEP_IDS.SKRIVEHASTIGHET,
			leseproven: YOUTH_TEST_NORMING_STEP_IDS['LESEPRØVEN'],
			ordforstaelse: YOUTH_TEST_NORMING_STEP_IDS['ORDFORSTÅELSE'],
			sprakforstaelse: YOUTH_TEST_NORMING_STEP_IDS['SPRAKFORSTÅELSE'],
		};

		const orderedIds = [
			ids.orddiktat,
			ids.ordkjedetesten,
			ids.skrivehastighet,
			ids.leseproven,
			ids.tulleordtesten,
			ids.sprakforstaelse,
			ids.ordforstaelse,
		];

		const subtestNamesById = {
			[ids.orddiktat]: 'Orddiktat',
			[ids.ordkjedetesten]: 'Ordkjedetest',
			[ids.tulleordtesten]: 'Tulleord',
			[ids.skrivehastighet]: 'Skrivehastighet',
			[ids.leseproven]: 'Leseprøven',
			[ids.ordforstaelse]: 'Ordforståelse',
			[ids.sprakforstaelse]: 'Setningsforståelse',
		};

		const scoresById = {};

		(testResult.subtests || []).forEach((subtest) => {
			if (!subtest) {
				return;
			}

			const rawScore = subtest.zScore;

			if (
				rawScore === null ||
				typeof rawScore === 'undefined' ||
				rawScore === ''
			) {
				return;
			}

			const score = Number(rawScore);

			if (!isNaN(score)) {
				scoresById[subtest.id] = score;
			}
		});

		const hasAllRequiredScores = orderedIds.every((id) =>
			Object.prototype.hasOwnProperty.call(scoresById, id)
		);

		if (!testResult.completedAt || !hasAllRequiredScores) {
			const incompleteText =
				testReportLang === 'nn'
					? strings._props.nn.localizedwriteuptextforIncompletedTest
					: strings.localizedwriteuptextforIncompletedTest;

			return strings.formatString(incompleteText, `${completeName}`);
		}

		const atOrBelow = (id, threshold) => scoresById[id] <= threshold;

		const dyslexiaIds = [
			ids.orddiktat,
			ids.ordkjedetesten,
			ids.skrivehastighet,
			ids.leseproven,
			ids.tulleordtesten,
		];

		const dyslexiaOtherIds = [
			ids.ordkjedetesten,
			ids.skrivehastighet,
			ids.leseproven,
			ids.tulleordtesten,
		];

		const dyslexiaSpecificIds = [
			ids.orddiktat,
			ids.ordkjedetesten,
			ids.skrivehastighet,
			ids.tulleordtesten,
		];

		const languageIds = [
			ids.leseproven,
			ids.sprakforstaelse,
			ids.ordforstaelse,
		];

		const languageSpecificIds = [ids.sprakforstaelse, ids.ordforstaelse];

		const lowIds = orderedIds.filter((id) => atOrBelow(id, -1.0));

		const dyslexiaLowCount = dyslexiaIds.filter((id) =>
			atOrBelow(id, -1.0)
		).length;

		const dyslexiaSevereOtherCount = dyslexiaOtherIds.filter((id) =>
			atOrBelow(id, -1.5)
		).length;

		const ordDiktatSevere = atOrBelow(ids.orddiktat, -1.5);

		const hasOtherDyslexiaLowScore = dyslexiaOtherIds.some((id) =>
			atOrBelow(id, -1.0)
		);

		const meetsDyslexiaProfile =
			dyslexiaLowCount >= 3 ||
			dyslexiaSevereOtherCount >= 2 ||
			(ordDiktatSevere && hasOtherDyslexiaLowScore);

		const languageLowCount = languageIds.filter((id) =>
			atOrBelow(id, -1.0)
		).length;

		const languageSevereCount = languageIds.filter((id) =>
			atOrBelow(id, -1.5)
		).length;

		const meetsLanguageProfile =
			languageLowCount === 3 || languageSevereCount >= 2;

		const hasDyslexiaSpecificLowScore = dyslexiaSpecificIds.some((id) =>
			atOrBelow(id, -1.0)
		);

		const hasLanguageSpecificLowScore = languageSpecificIds.some((id) =>
			atOrBelow(id, -1.0)
		);

		const isMixedProfile =
			lowIds.length >= 3 &&
			hasDyslexiaSpecificLowScore &&
			hasLanguageSpecificLowScore;

		const lowSubtestNames = this.formatDldYouthScreeningSubtestNames(
			lowIds,
			subtestNamesById
		);

		const youthScreeningStrings =
			testReportLang === 'nn' ? strings._props.nn : strings;

		if (isMixedProfile) {
			return this.formatDldScreeningText(
				youthScreeningStrings.dldYouthScreeningText5,
				lowSubtestNames,
				firstName,
				testResult.isTestOwner
			);
		}

		if (meetsDyslexiaProfile) {
			const dyslexiaText = atOrBelow(ids.leseproven, -1.0)
				? youthScreeningStrings.dldYouthScreeningText1
				: youthScreeningStrings.dldYouthScreeningText2;

			return this.formatDldScreeningText(
				dyslexiaText,
				lowSubtestNames,
				firstName,
				testResult.isTestOwner
			);
		}

		if (meetsLanguageProfile) {
			const languageText = atOrBelow(ids.leseproven, -1.0)
				? youthScreeningStrings.dldYouthScreeningText3
				: youthScreeningStrings.dldYouthScreeningText4;

			return this.formatDldScreeningText(
				languageText,
				lowSubtestNames,
				firstName,
				testResult.isTestOwner
			);
		}

		return youthScreeningStrings.dldYouthScreeningText6;
	}
	getDldChildScreeningWriteUpText(
		testResult,
		firstName,
		completeName,
		grade,
		testReportLang
	) {
		const numericGrade = Number(grade);
		console.log('CHILD SCREENING GRADE:', {
			grade,
			numericGrade,
		});

		const isGrade2To5 = numericGrade >= 2 && numericGrade <= 5;

		const isGrade6To7 = numericGrade >= 6 && numericGrade <= 7;

		if (!isGrade2To5 && !isGrade6To7) {
			return null;
		}

		const ids = {
			orddiktat: CHILD_SCRNG_DLD_TEST_STEP_IDS.ORDDIKTAT,
			ordkjedetesten: CHILD_SCRNG_DLD_TEST_STEP_IDS.ORDKJEDETESTEN,
			byttelydtesten: CHILD_SCRNG_DLD_TEST_STEP_IDS.BYTTELYDTESTEN,
			skrivehastighet: CHILD_SCRNG_DLD_TEST_STEP_IDS.SKRIVEHASTIGHET,
			leseproven: CHILD_SCRNG_DLD_TEST_STEP_IDS['LESEPRØVEN'],
			ordforstaelse: CHILD_SCRNG_DLD_TEST_STEP_IDS['ORDFORSTÅELSE'],
			sprakforstaelse: CHILD_SCRNG_DLD_TEST_STEP_IDS['SPRAKFORSTÅELSE'],
		};

		const orderedIds = [
			ids.orddiktat,
			ids.ordkjedetesten,
			ids.skrivehastighet,
			ids.leseproven,
			ids.byttelydtesten,
			ids.sprakforstaelse,
			ids.ordforstaelse,
		];

		const subtestNamesById =
			testReportLang === 'nn'
				? {
						[ids.orddiktat]: 'Orddiktat',
						[ids.ordkjedetesten]: 'Ordkjedetest',
						[ids.byttelydtesten]: 'Bytelyd',
						[ids.skrivehastighet]: 'Skrivehastigheit',
						[ids.leseproven]: 'Leseprøven',
						[ids.ordforstaelse]: 'Ordforståing',
						[ids.sprakforstaelse]: 'Språkforståing',
					}
				: {
						[ids.orddiktat]: 'Orddiktat',
						[ids.ordkjedetesten]: 'Ordkjedetest',
						[ids.byttelydtesten]: 'Byttelyd',
						[ids.skrivehastighet]: 'Skrivehastighet',
						[ids.leseproven]: 'Leseprøven',
						[ids.ordforstaelse]: 'Ordforståelse',
						[ids.sprakforstaelse]: 'Setningsforståelse',
					};

		const scoresById = {};

		(testResult.subtests || []).forEach((subtest) => {
			if (!subtest) {
				return;
			}

			const rawScore = subtest.zScore;

			if (
				rawScore === null ||
				typeof rawScore === 'undefined' ||
				rawScore === ''
			) {
				return;
			}

			const score = Number(rawScore);

			if (!isNaN(score)) {
				scoresById[subtest.id] = score;
			}
		});

		const hasAllRequiredScores = orderedIds.every((id) =>
			Object.prototype.hasOwnProperty.call(scoresById, id)
		);

		if (!testResult.completedAt || !hasAllRequiredScores) {
			const incompleteText =
				testReportLang === 'nn'
					? strings._props.nn.localizedwriteuptextforIncompletedTest
					: strings.localizedwriteuptextforIncompletedTest;

			return strings.formatString(incompleteText, `${completeName}`);
		}

		const atOrBelow = (id, threshold) => scoresById[id] <= threshold;

		const dyslexiaIds = [
			ids.orddiktat,
			ids.ordkjedetesten,
			ids.skrivehastighet,
			ids.leseproven,
			ids.byttelydtesten,
		];

		const dyslexiaOtherIds = [
			ids.ordkjedetesten,
			ids.skrivehastighet,
			ids.leseproven,
			ids.byttelydtesten,
		];

		const dyslexiaSpecificIds = [
			ids.orddiktat,
			ids.ordkjedetesten,
			ids.skrivehastighet,
			ids.byttelydtesten,
		];

		const languageIds = [
			ids.leseproven,
			ids.sprakforstaelse,
			ids.ordforstaelse,
		];

		const languageSpecificIds = [ids.sprakforstaelse, ids.ordforstaelse];

		const lowIds = orderedIds.filter((id) => atOrBelow(id, -1.0));

		const dyslexiaLowCount = dyslexiaIds.filter((id) =>
			atOrBelow(id, -1.0)
		).length;

		const dyslexiaSevereOtherCount = dyslexiaOtherIds.filter((id) =>
			atOrBelow(id, -1.5)
		).length;

		const ordDiktatSevere = atOrBelow(ids.orddiktat, -1.5);

		const hasOtherDyslexiaLowScore = dyslexiaOtherIds.some((id) =>
			atOrBelow(id, -1.0)
		);

		const meetsDyslexiaProfile =
			dyslexiaLowCount >= 3 ||
			dyslexiaSevereOtherCount >= 2 ||
			(ordDiktatSevere && hasOtherDyslexiaLowScore);

		const languageLowCount = languageIds.filter((id) =>
			atOrBelow(id, -1.0)
		).length;

		const languageSevereCount = languageIds.filter((id) =>
			atOrBelow(id, -1.5)
		).length;

		const meetsLanguageProfile =
			languageLowCount === 3 || languageSevereCount >= 2;

		const hasDyslexiaSpecificLowScore = dyslexiaSpecificIds.some((id) =>
			atOrBelow(id, -1.0)
		);

		const hasLanguageSpecificLowScore = languageSpecificIds.some((id) =>
			atOrBelow(id, -1.0)
		);

		const isMixedProfile =
			lowIds.length >= 3 &&
			hasDyslexiaSpecificLowScore &&
			hasLanguageSpecificLowScore;

		const lowSubtestNames = this.formatDldYouthScreeningSubtestNames(
			lowIds,
			subtestNamesById
		);
		const childScreeningStrings =
			testReportLang === 'nn' ? strings._props.nn : strings;

		const childScreeningTexts = isGrade2To5
			? {
					text1: childScreeningStrings.dldChildScreeningGrade2To5Text1,
					text2: childScreeningStrings.dldChildScreeningGrade2To5Text2,
					text3: childScreeningStrings.dldChildScreeningGrade2To5Text3,
					text4: childScreeningStrings.dldChildScreeningGrade2To5Text4,
					text5: childScreeningStrings.dldChildScreeningGrade2To5Text5,
					text6: childScreeningStrings.dldChildScreeningGrade2To5Text6,
				}
			: {
					text1: childScreeningStrings.dldChildScreeningGrade6To7Text1,
					text2: childScreeningStrings.dldChildScreeningGrade6To7Text2,
					text3: childScreeningStrings.dldChildScreeningGrade6To7Text3,
					text4: childScreeningStrings.dldChildScreeningGrade6To7Text4,
					text5: childScreeningStrings.dldChildScreeningGrade6To7Text5,
					text6: childScreeningStrings.dldChildScreeningGrade6To7Text6,
				};

		if (isMixedProfile) {
			return this.formatDldChildScreeningText(
				childScreeningTexts.text5,
				lowSubtestNames,
				firstName,
				testResult.isTestOwner,
				testReportLang
			);
		}

		if (meetsDyslexiaProfile) {
			const dyslexiaText = atOrBelow(ids.leseproven, -1.0)
				? childScreeningTexts.text1
				: childScreeningTexts.text2;

			return this.formatDldChildScreeningText(
				dyslexiaText,
				lowSubtestNames,
				firstName,
				testResult.isTestOwner,
				testReportLang
			);
		}

		if (meetsLanguageProfile) {
			const languageText = atOrBelow(ids.leseproven, -1.0)
				? childScreeningTexts.text3
				: childScreeningTexts.text4;

			return this.formatDldChildScreeningText(
				languageText,
				lowSubtestNames,
				firstName,
				testResult.isTestOwner,
				testReportLang
			);
		}

		return childScreeningTexts.text6;
	}
	// eslint-disable-next-line complexity
	render() {
		const {testResult, completeName, firstName, testReportLang, grade} =
			this.props;

		const hasSubtests =
			Array.isArray(testResult.subtests) &&
			testResult.subtests.length > 0;
		const language = hasSubtests
			? testResult.subtests[0].language
			: undefined;

		/*
		 * NEW DLD youth-screening assessment.
		 *
		 * Keep this before all legacy Assessment logic so the old thresholds
		 * and report strings remain completely untouched.
		 *
		 * TODO:
		 * Add approved Nynorsk versions of dldYouthScreeningText1-6 when
		 * available. Until then these profile texts intentionally use Bokmål.
		 */
		if (testResult.type === 'child-screening') {
			if (grade >= 2 && grade <= 7) {
				const writeUpText = this.getDldChildScreeningWriteUpText(
					testResult,
					firstName,
					completeName,
					grade,
					testReportLang
				);

				return (
					<div className="sub-test__scores">
						<div className="width--half center">
							<h4 className="sub-test__heading big">
								{strings.assessment}
							</h4>

							<p style={{whiteSpace: 'pre-line'}}>
								{writeUpText}
							</p>
						</div>
					</div>
				);
			}
		}

		if (testResult.type === 'youth-screening') {
			const writeUpText = this.getDldYouthScreeningWriteUpText(
				testResult,
				firstName,
				completeName,
				testReportLang
			);

			return (
				<div className="sub-test__scores">
					<div className="width--half center">
						<h4 className="sub-test__heading big">
							{strings.assessment}
						</h4>
						<p style={{whiteSpace: 'pre-line'}}>{writeUpText}</p>
					</div>
				</div>
			);
		}

		if (testResult.type === 'adult-screening') {
			const writeUpText = this.getDldAdultScreeningWriteUpText(
				testResult,
				firstName,
				completeName,
				testReportLang
			);

			return (
				<div className="sub-test__scores">
					<div className="width--half center">
						<h4 className="sub-test__heading big">
							{strings.assessment}
						</h4>

						<p style={{whiteSpace: 'pre-line'}}>{writeUpText}</p>
					</div>
				</div>
			);
		}

		const subtestsLength = testResult.subtests.length;
		const allLowZscores = {};
		const localizedwriteuptextforNormalresults =
			testReportLang === 'nn'
				? strings._props.nn.localizedwriteuptextforNormalresults
				: strings.localizedwriteuptextforNormalresults;
		const localizedwriteuptextforOneOtherSubTest =
			testReportLang === 'nn'
				? strings._props.nn.localizedwriteuptextforOneOtherSubTest
				: strings.localizedwriteuptextforOneOtherSubTest;
		const localizedwriteuptextforTwoOtherSubTest =
			testReportLang === 'nn'
				? strings._props.nn.localizedwriteuptextforTwoOtherSubTest
				: strings.localizedwriteuptextforTwoOtherSubTest;
		const localizedwriteuptextforThreeORMoreOtherSubTest =
			testReportLang === 'nn'
				? strings._props.nn
						.localizedwriteuptextforThreeORMoreOtherSubTest
				: strings.localizedwriteuptextforThreeORMoreOtherSubTest;
		const localizedwriteuptextforIncompletedTest =
			testReportLang === 'nn'
				? strings._props.nn.localizedwriteuptextforIncompletedTest
				: strings.localizedwriteuptextforIncompletedTest;
		const localizedwriteuptextforLowScoreOfOrdiktatWithOneOtherSubTest =
			testReportLang === 'nn'
				? strings._props.nn
						.localizedwriteuptextforLowScoreOfOrdiktatWithOneOtherSubTest
				: strings.localizedwriteuptextforLowScoreOfOrdiktatWithOneOtherSubTest;
		const localizedwriteuptextforLowScoreOfBetyledeWithOneOtherSubTest =
			testReportLang === 'nn'
				? strings._props.nn
						.localizedwriteuptextforLowScoreOfBetyledeWithOneOtherSubTest
				: strings.localizedwriteuptextforLowScoreOfBetyledeWithOneOtherSubTest;
		const localizedwriteuptextforLowScoreInLeseprovenAndOrdforstaelse =
			testReportLang === 'nn'
				? strings._props.nn
						.localizedwriteuptextforLowScoreInLeseprovenAndOrdforstaelse
				: strings.localizedwriteuptextforLowScoreInLeseprovenAndOrdforstaelse;
		let zScoreLowCount = 0;
		let writeUpText;
		let clearlyBelowZScoreCount = 0;
		let significantlyBelowZScoreCount = 0;
		let withInZScoreCount = 0;
		let clearlyAboveZScoreCount = 0;
		let testType;
		let stringForLowScoreInTwoSubtest;
		let stringForOnRemainingOnes;
		let stringForTheSubtests;
		let stringForResutls;

		if (language === 'nn') {
			stringForOnRemainingOnes = strings.remainingOnesNn;
			stringForTheSubtests = strings.subtestsNn;
			stringForResutls = strings.resultsNn;
		} else {
			stringForOnRemainingOnes = strings.remainingOnes;
			stringForTheSubtests = strings.subtestsWord;
			stringForResutls = strings.resultsWord;
		}

		if (testResult.type === 'child-screening') {
			testType = CHILD_SCRNG_DLD_TEST_STEP_IDS;
			stringForLowScoreInTwoSubtest = strings.formatString(
				localizedwriteuptextforLowScoreOfBetyledeWithOneOtherSubTest,
				`${getPronoun(testResult.testUser.gender, language)}`
			);
		} else {
			testType = YOUTH_TEST_NORMING_STEP_IDS;
			stringForLowScoreInTwoSubtest = strings.formatString(
				localizedwriteuptextforLowScoreOfOrdiktatWithOneOtherSubTest,
				`${firstName}`
			);
		}

		const findTestResult = testResult.subtests?.find((subtest) => {
			let subTestType;

			if (testType === CHILD_SCRNG_DLD_TEST_STEP_IDS) {
				subTestType = CHILD_SCRNG_DLD_TEST_STEP_IDS.BYTTELYDTESTEN;
			} else {
				subTestType = YOUTH_TEST_NORMING_STEP_IDS.ORDDIKTAT;
			}
			return subtest.id === subTestType;
		});

		testResult.subtests.forEach((subtest) => {
			if (subtest) {
				const {id, zScore} = subtest;
				if (zScore < -1) {
					zScoreLowCount++;
					allLowZscores[id] = zScore;
				}
				if (zScore > -2.0 && zScore < -1.01) {
					clearlyBelowZScoreCount++;
				} else if (zScore < -2.01) {
					significantlyBelowZScoreCount++;
				} else if (zScore > -1.0 && zScore < 1.0) {
					withInZScoreCount++;
				} else if (zScore > 1.01) {
					clearlyAboveZScoreCount++;
				}
			}
		});

		//maps test names with Zscores

		const allLowZscoresTestsWithNames = {};
		for (const [key, value] of Object.entries(testType)) {
			if (allLowZscores[value] !== undefined) {
				allLowZscoresTestsWithNames[key] = allLowZscores[value];
			}
		}
		const keys = Object.keys(allLowZscoresTestsWithNames);
		//if the the test is incomplete
		if (!hasSubtests) {
			// Bokmål / Nynorsk variants
			const line1_no = strings.formatString(
				strings.candidateNoSubtestsCompleted,
				completeName
			);
			const line2_no = strings.formatString(
				strings.candidateMustCompleteOneSubtest,
				completeName
			);

			const line1_nn = `${completeName} har ikkje gjennomført nokon deltestar.`;
			const line2_nn = `${completeName} må gjennomføre minst ein av deltestane for at vi skal kunne gje ei vurdering. Testen kan takast opp att frå «Mine testar».`;

			const pick = (bm, nn) => (testReportLang === 'nn' ? nn : bm);

			writeUpText = [
				pick(line1_no, line1_nn),
				pick(line2_no, line2_nn),
			].join('\n\n');
		} else if (testResult.completedAt === null) {
			writeUpText = strings.formatString(
				localizedwriteuptextforIncompletedTest,
				`${completeName}`
			);
		} else if (
			testResult.type !== 'child-screening' &&
			zScoreLowCount === 2 &&
			allLowZscoresTestsWithNames.hasOwnProperty('ORDFORSTÅELSE') &&
			allLowZscoresTestsWithNames.hasOwnProperty('LESEPRØVEN')
		) {
			writeUpText = strings.formatString(
				localizedwriteuptextforLowScoreInLeseprovenAndOrdforstaelse,
				`${completeName}`,
				clearlyBelowZScoreCount && !significantlyBelowZScoreCount
					? strings.formatString(
							strings.clearlyBelowOnNofSubtests,
							clearlyBelowZScoreCount,
							stringForTheSubtests
						)
					: '',
				significantlyBelowZScoreCount && !clearlyBelowZScoreCount
					? strings.formatString(
							strings.significantlyBelowOnNofSubtests,
							significantlyBelowZScoreCount,
							stringForTheSubtests
						)
					: '',
				clearlyBelowZScoreCount && significantlyBelowZScoreCount
					? strings.formatString(
							strings.clearlyAndSignificantlyBelowOnSubtests,
							clearlyBelowZScoreCount,
							stringForTheSubtests,
							significantlyBelowZScoreCount,
							stringForTheSubtests
						)
					: '',
				withInZScoreCount > 0 || clearlyAboveZScoreCount > 0
					? strings.formatString(
							strings.remainingSubtestsWithinOrAboveAverage,
							stringForOnRemainingOnes,
							stringForTheSubtests,
							firstName,
							stringForResutls
						)
					: '',
				`${getPronoun(testResult.testUser.gender, language)}`,
				<Link
					style={{color: 'blue'}}
					to={handBookPdf}
					target="_blank"
					rel="noopener noreferrer"
				>
					{strings.dysmateHandbook}
				</Link>
			);
		} else if (
			findTestResult?.zScore <= -1 ||
			zScoreLowCount >= TESTS_REQUIRED_TO_PASS
		) {
			//if the zscore are low in betelid test for child and ordiktat for youth and adult (only implemented for special case)
			switch (true) {
				//if only one tests have low zscores (betelid or ordiktat)
				case zScoreLowCount === 1:
					writeUpText = strings.formatString(
						localizedwriteuptextforNormalresults,
						`${completeName}`,
						withInZScoreCount && !clearlyAboveZScoreCount
							? strings.within
							: '',
						clearlyAboveZScoreCount && !withInZScoreCount
							? strings.clearlyover
							: '',
						withInZScoreCount && clearlyAboveZScoreCount
							? strings.withinAndClearlyOver
							: '',
						`${withInZScoreCount + clearlyAboveZScoreCount}`,
						`${getPronoun(testResult.testUser.gender, language)}`
					);
					break;
				//if only two tests have low zscores
				case zScoreLowCount === 2:
					writeUpText = strings.formatString(
						localizedwriteuptextforOneOtherSubTest,
						`${completeName}`,
						clearlyBelowZScoreCount &&
							!significantlyBelowZScoreCount
							? `klart under på ${clearlyBelowZScoreCount} av ${stringForTheSubtests}`
							: '',
						significantlyBelowZScoreCount &&
							!clearlyBelowZScoreCount
							? `betydelig under på ${significantlyBelowZScoreCount} av ${stringForTheSubtests}`
							: '',
						clearlyBelowZScoreCount && significantlyBelowZScoreCount
							? `klart under på ${clearlyBelowZScoreCount} av ${stringForTheSubtests} og betydelig under på ${significantlyBelowZScoreCount} av ${stringForTheSubtests} `
							: '',
						withInZScoreCount > 0 || clearlyAboveZScoreCount > 0
							? `På ${stringForOnRemainingOnes} ${stringForTheSubtests} oppnår ${firstName} ${stringForResutls} innenfor eller klart over gjennomsnittsområdet.`
							: '',
						stringForLowScoreInTwoSubtest,
						<Link
							style={{color: 'blue'}}
							to={handBookPdf}
							target="_blank"
							rel="noopener noreferrer"
						>
							Dysmate Håndbok
						</Link>
					);
					break;
				//if only three tests have low zscores
				case zScoreLowCount === 3:
					writeUpText = strings.formatString(
						localizedwriteuptextforTwoOtherSubTest,
						`${completeName}`,
						clearlyBelowZScoreCount &&
							!significantlyBelowZScoreCount
							? `klart under på ${clearlyBelowZScoreCount} av ${stringForTheSubtests}`
							: '',
						significantlyBelowZScoreCount &&
							!clearlyBelowZScoreCount
							? `betydelig under på ${significantlyBelowZScoreCount} av ${stringForTheSubtests}`
							: '',
						clearlyBelowZScoreCount && significantlyBelowZScoreCount
							? `klart under på ${clearlyBelowZScoreCount} av ${stringForTheSubtests} og betydelig under på ${significantlyBelowZScoreCount} av ${stringForTheSubtests} `
							: '',
						withInZScoreCount > 0 || clearlyAboveZScoreCount > 0
							? `På ${stringForOnRemainingOnes} ${stringForTheSubtests} oppnår ${firstName} ${stringForResutls} innenfor eller klart over gjennomsnittsområdet.`
							: '',
						`${getPronoun(testResult.testUser.gender, language)}`,
						`${formateTestName(keys[0])}`,
						`${formateTestName(keys[1])}`,
						`${formateTestName(keys[2])}`,
						`${firstName}`,
						<Link
							style={{color: 'blue'}}
							to={handBookPdf}
							target="_blank"
							rel="noopener noreferrer"
						>
							Dysmate Håndbok
						</Link>
					);
					break;
				//if more than three tests have low zscores
				case zScoreLowCount > 3:
					if (
						zScoreLowCount === 6 ||
						zScoreLowCount === 5 ||
						zScoreLowCount === 4
					) {
						const lastElement = keys[keys.length - 1];
						writeUpText = strings.formatString(
							localizedwriteuptextforThreeORMoreOtherSubTest,
							`${completeName}`,
							clearlyBelowZScoreCount &&
								!significantlyBelowZScoreCount
								? `klart under på ${clearlyBelowZScoreCount} av ${stringForTheSubtests}`
								: '',
							significantlyBelowZScoreCount &&
								!clearlyBelowZScoreCount
								? `betydelig under på ${significantlyBelowZScoreCount} av ${stringForTheSubtests}`
								: '',
							clearlyBelowZScoreCount &&
								significantlyBelowZScoreCount
								? `klart under på ${clearlyBelowZScoreCount} av ${stringForTheSubtests} og betydelig under på ${significantlyBelowZScoreCount} av ${stringForTheSubtests} `
								: '',
							withInZScoreCount > 0 || clearlyAboveZScoreCount > 0
								? `På ${stringForOnRemainingOnes} ${stringForTheSubtests} oppnår ${firstName} ${stringForResutls} innenfor eller klart over gjennomsnittsområdet.`
								: '',
							`${getPronoun(
								testResult.testUser.gender,
								language
							)}`,
							`${formateTestName(keys[0])}`,
							`${formateTestName(keys[1])}`,
							`${formateTestName(keys[2])}`,
							// eslint-disable-next-line no-nested-ternary
							keys[3]
								? keys[3] === lastElement
									? `og ${formateTestName(keys[3])}`
									: `, ${formateTestName(keys[3])}`
								: '',
							// eslint-disable-next-line no-nested-ternary
							keys[4]
								? keys[4] === lastElement
									? `og ${formateTestName(keys[4])}`
									: `, ${formateTestName(keys[4])}`
								: '',
							// eslint-disable-next-line no-nested-ternary
							keys[5]
								? keys[5] === lastElement
									? `og ${formateTestName(keys[5])}`
									: `, ${formateTestName(keys[5])}`
								: '',
							<Link
								style={{color: 'blue'}}
								to={handBookPdf}
								target="_blank"
								rel="noopener noreferrer"
							>
								Dysmate Håndbok
							</Link>
						);
					}
					break;
			}
		} else {
			//if the zscores are in normal range
			writeUpText = strings.formatString(
				localizedwriteuptextforNormalresults,
				`${completeName}`,
				withInZScoreCount && !clearlyAboveZScoreCount
					? strings.within
					: '',
				clearlyAboveZScoreCount && !withInZScoreCount
					? strings.clearlyover
					: '',
				withInZScoreCount && clearlyAboveZScoreCount
					? strings.withinAndClearlyOver
					: '',
				`${withInZScoreCount + clearlyAboveZScoreCount}`,
				`${getPronoun(testResult.testUser.gender, language)}`
			);
		}
		return (
			<div className="sub-test__scores">
				<div className="width--half center">
					<h4 className="sub-test__heading big">
						{strings.assessment}
					</h4>
					<p>{writeUpText}</p>
				</div>
			</div>
		);
	}
}

Assessment.propTypes = {
	currentUser: PropTypes.shape({}),
};

const mapStateToProps = (state) => {
	const {currentUser} = state;

	return {
		currentUser,
	};
};

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(Assessment);
